import { useRef, useEffect, useState } from 'react';
import SignaturePad from 'signature_pad';

const SignaturePadComponent = ({ onSave }: { onSave: (signature: string) => void }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);
  const [hasSignature, setHasSignature] = useState(false);

  // Resize canvas for high DPI screens
  const resizeCanvas = () => {
    const canvas = canvasRef.current;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    
    if (!canvas || !signaturePadRef.current) return;

    // Store the current signature data
    const oldData = signaturePadRef.current.toData();
    
    // Resize canvas
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(ratio, ratio);
    }
    
    // Restore the signature data
    signaturePadRef.current.clear();
    if (oldData && oldData.length > 0) {
      signaturePadRef.current.fromData(oldData);
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const signaturePad = new SignaturePad(canvas, {
      minWidth: 2,
      maxWidth: 4,
      penColor: 'black',
    });

    signaturePadRef.current = signaturePad;

    // Add event listener for signature changes
    const handleEndStroke = () => {
      const isEmpty = signaturePad.isEmpty();
      setHasSignature(!isEmpty);
      if (!isEmpty) {
        const base64 = signaturePad.toDataURL('image/png');
        onSave(base64);
      }
    };

    signaturePad.addEventListener('endStroke', handleEndStroke);

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      signaturePad.removeEventListener('endStroke', handleEndStroke);
      signaturePad.off();
    };
  }, [onSave]);

  const handleClear = () => {
    if (!signaturePadRef.current) return;
    signaturePadRef.current.clear();
    setHasSignature(false);
    if (onSave) onSave('');
  };

  return (
    <div className="signature-pad-container">
      <div style={{ border: '1px solid #ccc' }} id="signature_pad">
        <canvas
          ref={canvasRef}
          style={{
            width: '100%',
            height: '200px',
            display: 'block',
            touchAction: 'none',
          }}
        />
      </div>
      <div style={{ 
        marginTop: '10px', 
        display: 'flex',
        justifyContent: 'flex-end'
      }}>
        <button 
          onClick={handleClear} 
          style={{
            display: 'inline-block',
            padding: '8px 16px',
            backgroundColor: hasSignature ? '#dc3545' : '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            transition: 'background-color 0.2s'
          }}
        >
          Clear
        </button>
      </div>
    </div>
  );
};

export default SignaturePadComponent;
